// ══════════════════════════════════════════════════════════════════════════════════════════════
// L'ÉDITEUR DE CORPS D'E-MAIL — la surface, partagée par toutes les applications web.
//
// POURQUOI IL EST ICI. Il existait déjà, **enfermé dans l'assistant marketing** du back-office. Le
// second consommateur arrive (bibliothèque de gabarits côté magasin, courrier de la Console) : la
// règle de 2 dit d'EXTRAIRE, pas de recopier — deux éditeurs divergeraient à la première correction,
// et c'est la mise en forme d'un message envoyé à des clients.
//
// ⚠️ 🔴 IL EST NON CONTRÔLÉ, ET C'EST UNE CONTRAINTE, PAS UN CHOIX DE CONFORT. Réécrire le `innerHTML`
// d'un `contentEditable` à chaque frappe REPOSE LE CURSEUR au début : le texte se saisit à l'envers.
// Le corps initial est donc SEMÉ UNE FOIS, et les frappes suivantes ne reviennent jamais dans le DOM.
// ⚠️ Pour charger un AUTRE gabarit, on remonte le composant avec une `key` — c'est explicite, et ça
// évite d'inventer une règle « quand re-semer ? » qui se tromperait un jour pendant une frappe.
//
// ⚠️ 🔴 LE COLLAGE EST ASSAINI. Un `contentEditable` accepte n'importe quel HTML collé : sans ce
// filtre, un `<script>` collé vivrait dans l'aperçu en direct — c'est-à-dire s'exécuterait dans le
// navigateur de celui qui compose — et partirait en base. Le serveur assainit aussi, parce que l'API
// est la vraie porte ; ici on protège l'aperçu et on garde ce qu'on range proche de ce qui est permis.
//
// ⚠️ LA BARRE AGIT VRAIMENT (`document.execCommand`), et ses boutons préviennent le `mousedown` :
// sans ça, le clic vole le focus au texte et la sélection disparaît avant la commande.
//
// ⚠️ 🔴 IL NE CONNAÎT AUCUN CHAMP DE FUSION. `{prénom}` n'est pas un mot d'ici : chaque application
// injecte SES jetons, avec le motif de ceux qui ne peuvent pas encore être remplis. Un éditeur qui
// connaîtrait les champs du marketing les proposerait dans le courrier d'une facture.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useEffect, useRef, type CSSProperties } from 'react';
import { sanitizeHtml } from '@orgazone/email-template';
import { key, t } from '@orgazone/i18n';
import { OZ_INTERACTIVE, OZ_INTERACTIVE_SURFACE } from './interactive';
import { Tooltip } from './tooltip';

/** Un jeton insérable, tel que l'application le déclare. */
export interface EmailEditorField {
  /** Le jeton TEL QU'IL S'ÉCRIT, accolades comprises — ex. `{prénom}`. */
  token: string;
  /**
   * Motif quand le champ ne peut PAS encore être rempli ; présent ⇒ pastille désactivée.
   * ⚠️ 🔴 C'EST UNE CLÉ, ET L'ÉDITEUR LA RÉSOUT. L'application la DÉCLARE avec `key()` — la résoudre
   * chez elle figerait le mot dans la langue du CHARGEMENT du module. Rendue nue, elle afficherait
   * la clé à l'opérateur (Forme DL) ; le contrôle de complétude ne peut pas la réclamer, puisqu'elle
   * n'est littérale nulle part ici — c'est le prix connu du partage, le même que pour les droits.
   * ⚠️ On montre le jeton plutôt que de le cacher : l'effacer ferait croire qu'il n'existe pas, alors
   * qu'il est prévu et que le moteur manque. Le motif dit lequel des deux (§23.0).
   */
  reason?: string;
  /** Le gabarit doit-il le citer ? Marqué d'un astérisque dans l'habillage « carte ». */
  required?: boolean;
  /**
   * L'explication au survol de la pastille — DÉJÀ résolue par l'application, qui la compose (motif,
   * repli, obligation) : elle seule connaît ce que le champ vaut.
   */
  tip?: string;
}

export interface EmailEditorProps {
  /**
   * Le corps INITIAL, semé UNE FOIS au montage.
   * ⚠️ Ce n'est pas une valeur contrôlée : voir l'en-tête. Pour charger un autre gabarit, remonter le
   * composant avec une `key`.
   */
  initialValue?: string;
  /** Appelé à chaque frappe avec le HTML courant. */
  onChange: (html: string) => void;
  /** Les jetons proposés — aucun par défaut, parce que le composant n'en connaît aucun. */
  fields?: readonly EmailEditorField[];
  /**
   * Motif quand l'insertion d'image n'est pas disponible ; ABSENT ⇒ le bouton n'est pas rendu.
   * ⚠️ Clé déclarée par l'application, RÉSOLUE ici — voir `EmailEditorField.reason`.
   * ⚠️ On ne rend JAMAIS un bouton d'image actif : une image collée dans un corps ne s'affiche chez
   * aucun destinataire sans hébergement des pièces jointes.
   */
  imageReason?: string;
  /** Ce que lit un lecteur d'écran — l'application sait de quel message il s'agit, pas nous. */
  ariaLabel: string;
  minHeight?: number;
  /**
   * `plain` (défaut) : barre et champ séparés, bordés — l'assistant de campagne.
   * `card` : barre INTÉGRÉE en tête, champ sans bordure — l'application pose la carte autour, et y
   * ajoute ce qui suit l'éditeur (problèmes, aide). Le dessin de la bibliothèque de gabarits.
   * ⚠️ DEUX HABILLAGES, UN SEUL ÉDITEUR : le semis unique, le collage assaini et les commandes restent
   * les mêmes — c'est ce qui ne doit pas diverger.
   */
  look?: 'plain' | 'card';
}

const BTN: CSSProperties = {
  height: 28,
  border: '1px solid var(--ink-200)',
  borderRadius: 'var(--r-xs)',
  background: 'var(--paper)',
  color: 'var(--ink-700)',
  cursor: 'pointer',
};

const SEPARATEUR: CSSProperties = { width: 1, background: 'var(--ink-200)', margin: '2px 3px' };

/**
 * Les commandes de mise en forme — la LETTRE du bouton, ce qu'un lecteur d'écran en DIT, et la
 * commande exécutée.
 *
 * ⚠️ 🔴 LA LETTRE N'EST PAS UN LIBELLÉ. « G » ne dit rien à qui n'a pas l'écran sous les yeux, et la
 * passer à `t()` en ferait une clé CONSTRUITE — qu'aucun contrôle de complétude ne peut réclamer
 * (Forme DL). Chaque bouton porte donc son mot, et la lettre reste un dessin.
 *
 * ⚠️ `key()` DÉCLARE, `t()` RÉSOUT au rendu. Écrire le mot nu ici le laisserait hors du catalogue ;
 * l'écrire avec `t()` le figerait dans la langue du CHARGEMENT du module, avant qu'on sache celle
 * du compte.
 */
const FORMES = [
  { cmd: 'bold', l: 'G', mot: key('Gras'), st: { fontWeight: 800 } },
  { cmd: 'italic', l: 'I', mot: key('Italique'), st: { fontStyle: 'italic', fontWeight: 600 } },
  { cmd: 'underline', l: 'S', mot: key('Souligné'), st: { textDecoration: 'underline', fontWeight: 600 } },
] as const;

/** Un outil de la barre intégrée (habillage « carte ») — sans bordure, il se lit sur le fond de la barre. */
const OUTIL: CSSProperties = {
  height: 30, minWidth: 30, padding: '0 8px', border: 'none', borderRadius: 'var(--r-xs)',
  background: 'transparent', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 700,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};

const SEPARATEUR_CARTE: CSSProperties = { width: 1, height: 20, background: 'var(--ink-200)', margin: '0 4px' };

/**
 * Les trois tailles de l'habillage « carte » — la commande, la taille du A dessiné (`null` = celle du
 * bouton), et le mot de l'infobulle.
 */
const TAILLES_CARTE = [
  { sz: '2', fs: 'var(--t-meta)', mot: key('Petit') },
  { sz: '3', fs: null, mot: key('Normal') },
  { sz: '5', fs: 16, mot: key('Grand') },
] as const;

/**
 * Les trois tailles de `execCommand` retenues, la taille d'affichage de leur bouton, et son NOM — trois boutons « A »
 * s'annonçaient tous « Taille du texte », sans dire laquelle.
 */
const TAILLES = [
  ['2', 10, key('Taille du texte : petite')],
  ['3', 12.5, key('Taille du texte : normale')],
  ['5', 15, key('Taille du texte : grande')],
] as const;

/**
 * L'ÉDITEUR.
 * ⚠️ `onChange` est appelé avec le HTML BRUT du DOM : c'est le serveur qui l'assainit avant de le
 * ranger, et le collage est déjà filtré ici. L'appelant n'a donc jamais à nettoyer lui-même — et
 * surtout pas à s'en remettre à nous pour la sécurité, puisque l'API est la vraie porte.
 */
export function EmailEditor({
  initialValue, onChange, fields = [], imageReason, ariaLabel, minHeight = 190, look = 'plain',
}: EmailEditorProps) {
  const zone = useRef<HTMLDivElement | null>(null);

  // ⚠️ SEMÉ UNE FOIS. Le tableau de dépendances est VIDE à dessein : re-semer à chaque changement de
  //    `initialValue` remettrait le curseur au début pendant la frappe.
  useEffect(() => {
    if (zone.current && initialValue) zone.current.innerHTML = sanitizeHtml(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dire = () => onChange(zone.current?.innerHTML ?? '');

  /** Exécute une commande sans perdre la sélection, puis rend le corps à l'appelant. */
  const commande = (cmd: string, valeur?: string) => {
    document.execCommand(cmd, false, valeur);
    dire();
  };

  /**
   * Insère un jeton À L'ENDROIT DU CURSEUR.
   * ⚠️ `insertText` ET NON une écriture du `innerHTML` : la commande respecte la sélection courante et
   * entre dans la pile d'annulation du navigateur. Recomposer le HTML à la main perdrait les deux.
   */
  const inserer = (jeton: string) => {
    zone.current?.focus();
    commande('insertText', jeton);
  };

  /**
   * ⚠️ 🔴 LE COLLAGE PASSE PAR L'ASSAINISSEUR. Sans ça, un `<script>` collé depuis une page web vivrait
   * dans l'aperçu en direct — donc s'exécuterait chez celui qui compose — et partirait en base.
   */
  const coller = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const brut = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
    commande('insertHTML', sanitizeHtml(brut));
  };

  const zoneProps = {
    ref: zone,
    contentEditable: true,
    suppressContentEditableWarning: true,
    role: 'textbox',
    'aria-multiline': true,
    'aria-label': ariaLabel,
    onInput: dire,
    onPaste: coller,
  } as const;

  if (look === 'card') {
    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 3, padding: '8px 10px', flexWrap: 'wrap',
          borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)',
        }}>
          {FORMES.map((f) => (
            <Tooltip key={f.cmd} content={t(f.mot)}>
              <button type="button" className={OZ_INTERACTIVE_SURFACE} aria-label={t(f.mot)}
                onMouseDown={(e) => { e.preventDefault(); commande(f.cmd); }}
                style={OUTIL}>
                {f.cmd === 'bold' ? <b>{f.l}</b> : f.cmd === 'italic' ? <i>{f.l}</i> : <u>{f.l}</u>}
              </button>
            </Tooltip>
          ))}
          <span style={SEPARATEUR_CARTE} />
          {TAILLES_CARTE.map((z) => (
            <Tooltip key={z.sz} content={t(z.mot)}>
              <button type="button" className={OZ_INTERACTIVE_SURFACE} aria-label={t(z.mot)}
                onMouseDown={(e) => { e.preventDefault(); commande('fontSize', z.sz); }}
                style={OUTIL}>
                {z.fs === null ? 'A' : <span style={{ fontSize: z.fs }}>A</span>}
              </button>
            </Tooltip>
          ))}
          {fields.length > 0 ? (
            <>
              <span style={SEPARATEUR_CARTE} />
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em',
                textTransform: 'uppercase', color: 'var(--ink-500)', margin: '0 4px',
              }}>{t('Insérer')}</span>
              {fields.map((f) => (
                <Tooltip key={f.token} content={f.reason !== undefined ? t(f.reason) : f.tip}>
                  <button type="button" className={OZ_INTERACTIVE} disabled={f.reason !== undefined}
                    onMouseDown={(e) => { e.preventDefault(); inserer(f.token); }}
                    style={{
                      height: 26, padding: '0 9px', border: '1px solid var(--teal-400)',
                      borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)',
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700,
                      whiteSpace: 'nowrap', cursor: f.reason === undefined ? 'pointer' : 'not-allowed',
                      opacity: f.reason === undefined ? 1 : 0.5,
                    }}>
                    {f.token}
                    {f.required ? <span style={{ marginLeft: 4, color: 'var(--rose-fg)' }}>*</span> : null}
                  </button>
                </Tooltip>
              ))}
            </>
          ) : null}
        </div>
        <div {...zoneProps} style={{
          minHeight, padding: '16px 18px', fontSize: 'var(--t-control)', lineHeight: 1.6,
          color: 'var(--ink-900)', outline: 'none', overflowY: 'auto',
        }} />
      </>
    );
  }

  return (
    <>
      <span style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
        {FORMES.map((f) => (
          <button key={f.cmd} type="button" aria-label={t(f.mot)} title={t(f.mot)}
            onMouseDown={(e) => { e.preventDefault(); commande(f.cmd); }}
            style={{ ...BTN, width: 30, fontSize: 'var(--t-body)', ...f.st }}>{f.l}</button>
        ))}
        <span style={SEPARATEUR} />
        {TAILLES.map(([sz, fs, mot]) => (
          <button key={sz} type="button" aria-label={t(mot)}
            onMouseDown={(e) => { e.preventDefault(); commande('fontSize', sz); }}
            style={{ ...BTN, padding: '0 8px', fontSize: fs, fontWeight: sz === '5' ? 800 : 700 }}>A</button>
        ))}
        {imageReason ? (
          <>
            <span style={SEPARATEUR} />
            <button type="button" disabled title={t(imageReason)}
              style={{ ...BTN, padding: '0 9px', cursor: 'not-allowed', opacity: 0.5, fontSize: 'var(--t-meta)', fontWeight: 600 }}>
              {t('Image')}
            </button>
          </>
        ) : null}
      </span>

      {fields.length > 0 ? (
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 7 }}>
          {fields.map((f) => (
            <button key={f.token} type="button" disabled={f.reason !== undefined}
              {...(f.reason === undefined ? {} : { title: t(f.reason) })}
              onMouseDown={(e) => { e.preventDefault(); inserer(f.token); }}
              style={{
                ...BTN, height: 24, padding: '0 9px', border: '1px dashed var(--ink-300)',
                borderRadius: 'var(--r-pill)', background: 'var(--ink-50)', color: 'var(--ink-600)',
                fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)',
                cursor: f.reason === undefined ? 'pointer' : 'not-allowed',
                opacity: f.reason === undefined ? 1 : 0.5,
              }}>{f.token}</button>
          ))}
        </span>
      ) : null}

      <div
        ref={zone}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline
        aria-label={ariaLabel}
        onInput={dire}
        onPaste={coller}
        style={{
          minHeight, padding: '12px 14px', border: '1px solid var(--ink-200)',
          borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-lead)',
          color: 'var(--ink-900)', lineHeight: 1.6, overflowY: 'auto',
        }}
      />
    </>
  );
}
